import axios from 'axios';

const trimString = (value) => String(value || '').trim();

export const parseWebsiteColors = (value) =>
  trimString(value)
    .split(/\s*(?:,|\band\b)\s*/i)
    .map((color) => color.trim())
    .filter(Boolean);

const getFieldValue = (fields, index) => {
  const value = fields?.vals?.[index];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const getSocialLink = (fields, index) =>
  fields?.none?.[index] ? null : getFieldValue(fields, index);

export const buildZohoOnboardingRequest = ({
  zohoAccountId,
  values = {},
  web = {},
  hasExistingWebsite = true
}) => {
  const domainFields = values.domain;
  const domainValues = domainFields?.unsure
    ? null
    : {
        registrar: getFieldValue(domainFields, 0),
        username: getFieldValue(domainFields, 1),
        password: getFieldValue(domainFields, 2)
      };
  const domain =
    domainValues && Object.values(domainValues).some(Boolean)
      ? Object.fromEntries(
          Object.entries(domainValues).filter(([, value]) => value !== null)
        )
      : null;

  const additionalAnswer = hasExistingWebsite
    ? web.content
    : "I don't have a website";

  const request = {
    zohoAccountId,
    domain,
    socialLinks: {
      facebook: getSocialLink(values.socialLinks, 0),
      instagram: getSocialLink(values.socialLinks, 1),
      tiktok: getSocialLink(values.socialLinks, 2)
    },
    website: {
      template: web.layout,
      colors: parseWebsiteColors(web.colors),
      additionalQuestion: {
        label: 'Website content preference',
        answer: additionalAnswer
      }
    }
  };

  validateWebsitePreferences({
    zohoAccountId: request.zohoAccountId,
    template: request.website.template,
    colors: request.website.colors,
    additionalQuestion: request.website.additionalQuestion.answer
  });

  return request;
};

export const validateWebsitePreferences = ({
  zohoAccountId,
  template,
  colors,
  additionalQuestion
}) => {
  if (typeof zohoAccountId !== 'string' || !trimString(zohoAccountId)) {
    throw new Error('A linked Zoho Account is required before submitting.');
  }
  if (!trimString(template)) {
    throw new Error('Select a website layout before submitting.');
  }
  if (
    !Array.isArray(colors) ||
    colors.length === 0 ||
    colors.some((color) => !trimString(color))
  ) {
    throw new Error('Enter at least one website color before submitting.');
  }
  if (!trimString(additionalQuestion)) {
    throw new Error('Answer the website content question before submitting.');
  }
};

export const submitWebsitePreferences = async ({
  endpoint,
  accessToken,
  request
}) => {
  if (!trimString(endpoint)) {
    throw new Error('The Zoho website note service is not configured.');
  }

  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    if (trimString(accessToken)) {
      headers.Authorization = `bearer ${accessToken}`;
    }
    const response = await axios.post(endpoint, request, {
      headers
    });
    const data = response.data || {};
    const hasConfirmedRecords = Boolean(data.zohoCaseId && data.zohoTaskId);

    if (!hasConfirmedRecords) {
      const error = new Error(
        'Zoho did not confirm that the onboarding submission was completed.'
      );
      error.noteOutcome = 'unknown';
      throw error;
    }

    return data;
  } catch (error) {
    if (error.noteOutcome) throw error;

    const responseData = error.response?.data;
    const enrichedError = new Error(
      responseData?.message ||
        error.message ||
        'The Zoho onboarding submission could not be completed.'
    );
    enrichedError.noteOutcome =
      responseData?.noteOutcome === 'not_created' ? 'not_created' : 'unknown';
    enrichedError.cause = error;
    throw enrichedError;
  }
};
